import React, { useState } from 'react';
import { Button, Collapse, Form, Modal, Popconfirm, Radio, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TGDChoice, TGDDesign, TGDRule, TGDVersion } from
  '../../../models/courses/graphicDesign';
import GDEditor from '../editor/GDEditor';
import GDStage from '../canvas/GDStage';
import LocalizedInput, { TAdminLanguage } from './LocalizedInput';
import RulesEditor from './RulesEditor';
import { DESIGN_RULE_TYPES, createDefaultRule } from './ruleTemplates';

interface Props { versions: TGDVersion[]; onVersions: (items: TGDVersion[]) => void;
  choices: TGDChoice[]; onChoices: (items: TGDChoice[]) => void;
  rules: TGDRule[]; onRules: (rules: TGDRule[]) => void;
  proofRules: TGDRule[]; onProofRules: (rules: TGDRule[]) => void;
  artboard: { width: number; height: number }; background: string;
  intent: boolean; readOnly?: boolean; language: TAdminLanguage }
const VersionsEditor: React.FC<Props> = ({ versions, onVersions, choices, onChoices,
  rules, onRules, proofRules, onProofRules, artboard, background, intent, readOnly,
  language }) => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const updateVersion = (id: string, patch: Partial<TGDVersion>) =>
    onVersions(versions.map(item => item.id === id ? { ...item, ...patch } : item));
  const updateChoice = (id: string, patch: Partial<TGDChoice>) =>
    onChoices(choices.map(item => item.id === id ? { ...item, ...patch } : item));
  const addVersion = (source?: TGDVersion) => {
    let number = 1;
    while (versions.some(item => item.id === `v${number}`)) number += 1;
    const design: TGDDesign = source ? structuredClone(source.design)
      : { background, components: [] };
    onVersions([...versions, { id: `v${number}`, label: { fr: `Version ${number}` }, design }]);
  };
  const addChoice = () => {
    let number = 1;
    while (choices.some(item => item.id === `c${number}`)) number += 1;
    onChoices([...choices, { id: `c${number}`, label: { fr: '' } }]);
  };
  const expected = (type: 'choiceEquals' | 'reasonEquals', id: string) => {
    const existing = rules.find(rule => rule.type === type);
    if (existing) onRules(rules.map(rule => rule.id === existing.id ? {
      ...rule, params: type === 'choiceEquals' ? { choiceId: id } : { reasonId: id },
    } as TGDRule : rule));
    else {
      const next = createDefaultRule(type, rules.map(rule => rule.id));
      onRules([...rules, { ...next,
        params: type === 'choiceEquals' ? { choiceId: id } : { reasonId: id },
      } as TGDRule]);
    }
  };
  const editor = versions.find(version => version.id === editing);
  const choiceRule = rules.find(rule => rule.type === 'choiceEquals');
  const reasonRule = rules.find(rule => rule.type === 'reasonEquals');
  return <div style={{ display: 'grid', gap: 24 }}>
    <section><h3>{t('graphicDesignLesson.admin.versions')}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {versions.map(version => <div key={version.id} style={{ width: 240 }}>
          <div className="gd-stage-frame" style={{ maxWidth: 240,
            aspectRatio: `${artboard.width} / ${artboard.height}` }}>
            <GDStage design={version.design} artboard={artboard} selectedId={null} readOnly
              zoom="fit" direction={i18n.dir()} onSelect={() => undefined}
              onPreview={() => undefined} onCommit={() => undefined} />
          </div>
          <Form layout="vertical"><Form.Item label={t('graphicDesignLesson.admin.versionLabel')}>
            <LocalizedInput value={version.label} language={language} readOnly={readOnly}
              onChange={label => updateVersion(version.id, { label })} />
          </Form.Item></Form>
          {!intent && <Radio checked={choiceRule?.type === 'choiceEquals' &&
            choiceRule.params.choiceId === version.id} disabled={readOnly}
            onChange={() => expected('choiceEquals', version.id)}>
            {t('graphicDesignLesson.admin.correctVersion')}</Radio>}
          <Space wrap style={{ marginTop: 8 }}>
            <Button onClick={() => setEditing(version.id)}>{t('graphicDesignLesson.admin.editVisual')}</Button>
            {!intent && <Button disabled={readOnly || versions.length >= 3}
              onClick={() => addVersion(version)}>{t('graphicDesignLesson.admin.duplicate')}</Button>}
            {!intent && <Popconfirm title={t('graphicDesignLesson.admin.deleteVersionConfirm')}
              onConfirm={() => onVersions(versions.filter(item => item.id !== version.id))}>
              <Button danger disabled={readOnly}>{t('graphicDesignLesson.admin.delete')}</Button>
            </Popconfirm>}
          </Space>
        </div>)}
        {versions.length < (intent ? 1 : 3) && <Button type="dashed" disabled={readOnly}
          style={{ width: 240, minHeight: 100 }} onClick={() => addVersion()}>
          {t('graphicDesignLesson.admin.addVersionNew')}</Button>}
      </div>
    </section>
    <section><h3>{t(intent ? 'graphicDesignLesson.admin.wordsTitle'
      : 'graphicDesignLesson.admin.reasonsTitle')}</h3>
      <Typography.Text type="secondary">{t(intent ? 'graphicDesignLesson.admin.wordsHelp'
        : 'graphicDesignLesson.admin.reasonsHelp')}</Typography.Text>
      {choices.map(choice => <div key={choice.id} className="gd-admin-row">
        <Radio aria-label={t(intent ? 'graphicDesignLesson.admin.expectedWordNew'
          : 'graphicDesignLesson.admin.correctReason')}
          checked={intent ? choiceRule?.type === 'choiceEquals' &&
            choiceRule.params.choiceId === choice.id : reasonRule?.type === 'reasonEquals' &&
            reasonRule.params.reasonId === choice.id} disabled={readOnly}
          onChange={() => expected(intent ? 'choiceEquals' : 'reasonEquals', choice.id)} />
        <div style={{ flex: 1 }}><LocalizedInput value={choice.label} language={language}
          readOnly={readOnly} onChange={label => updateChoice(choice.id, { label })} /></div>
        <Button type="text" icon={<DeleteOutlined />} aria-label={t('graphicDesignLesson.admin.delete')}
          disabled={readOnly} onClick={() => onChoices(choices.filter(item => item.id !== choice.id))} />
      </div>)}
      <Button type="dashed" disabled={readOnly} onClick={addChoice}>
        {t('graphicDesignLesson.admin.add')}</Button>
    </section>
    {!intent && <Collapse items={[{ key: 'proof',
      label: t('graphicDesignLesson.admin.proofOptional'), children: <>
        <Typography.Text type="secondary">{t('graphicDesignLesson.admin.proofHelp')}</Typography.Text>
        <RulesEditor rules={proofRules} onChange={onProofRules} allowedTypes={DESIGN_RULE_TYPES}
          solution={versions[0]?.design} readOnly={readOnly} language={language} />
      </> }]} />}
    <Modal open={!!editor} title={editor?.label?.fr || editor?.id} width="100vw"
      style={{ top: 0, maxWidth: '100vw' }} footer={null} onCancel={() => setEditing(null)}
      destroyOnClose>
      {editor && <GDEditor initialDesign={editor.design} artboard={artboard}
        constraints={{ allowedTools: ['shape', 'pictogram', 'text', 'palette', 'layers',
          'gradient'] }} readOnly={readOnly}
        onChange={design => updateVersion(editor.id, { design })} />}
    </Modal>
  </div>;
};
export default VersionsEditor;
